import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';
import { keyframeAnimation } from '../utils/styledUtils';

// Make sure all keyframes are defined first
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

// KEY CHANGE: Use a consistent approach for animation using the css helper
// Using the correct syntax for animation with keyframes
const DataContainer = styled.div`
  padding: 6rem 2rem;
  background-color: ${props => props.theme.colors.backgroundAlt};
`;

const ChartContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.medium};
  margin-bottom: 2rem;
  opacity: 0;
  /* KEY CHANGE: Proper keyframe usage with css helper */
  animation: ${props => css`${fadeIn} 0.5s ease forwards`};
  animation-delay: ${props => props.delay || '0s'};
`;

const DataPoint = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 10px;
  /* KEY CHANGE: Proper keyframe usage with css helper */
  animation: ${props => css`${pulse} 2s ease-in-out infinite`};
`;

const AnimatedIcon = styled.div`
  width: 40px;
  height: 40px;
  /* KEY CHANGE: Proper keyframe usage with css helper */
  animation: ${props => css`${rotate} 5s linear infinite`};
`;

const FloatingElement = styled.div`
  position: relative;
  /* KEY CHANGE: Proper keyframe usage with css helper */
  animation: ${props => css`${float} 3s ease-in-out infinite`};
`;

// Additional styles with proper animation handling
const MapContainer = styled.div`
  position: relative;
  height: 500px;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background-color: #e8f4fc;
  opacity: 0;
  /* KEY CHANGE: Proper keyframe usage with css helper */
  animation: ${props => css`${fadeIn} 0.8s ease forwards`};
  animation-delay: 0.3s;
`;

const DataVisualization = () => {
  const { translate } = useLanguage();
  const [activeChart, setActiveChart] = useState('temperature');

  return (
    <section id="data">
      <DataContainer>
        <div className="container">
          <h2>{translate('data.title', 'Weather Data Visualization')}</h2>
          <p>{translate('data.description', 'Interactive visualization of community weather data')}</p>
          
          <MapContainer>
            {/* Map content */}
            <div className="map-overlay">
              <DataPoint />
              <AnimatedIcon />
              <FloatingElement />
            </div>
          </MapContainer>
          
          <div className="chart-row">
            <ChartContainer delay="0.2s">
              <h3>{translate('data.temperature', 'Temperature')}</h3>
              {/* Chart content */}
            </ChartContainer>
            
            <ChartContainer delay="0.4s">
              <h3>{translate('data.precipitation', 'Precipitation')}</h3>
              {/* Chart content */}
            </ChartContainer>
            
            <ChartContainer delay="0.6s">
              <h3>{translate('data.wind', 'Wind Speed')}</h3>
              {/* Chart content */}
            </ChartContainer>
          </div>
        </div>
      </DataContainer>
    </section>
  );
};

export default DataVisualization;